import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { ShieldCheck, MapPin, Star, Heart, Flag, Phone, MessageSquare, Clock, Check, MessageCircle, Send, Mail, Copy } from "lucide-react";
import VerifiedRibbon from "@/components/VerifiedRibbon";
import { toast } from "sonner";
import {
  activeContactChannels, whatsappHref, telegramHref, telHref, mailtoHref, viberHref,
  maskPhone, toInternational,
} from "@/lib/contact";
import { resolveImageUrl, onImageError } from "@/lib/img";

const AVAILABILITY_LABELS = {
  incall: "Incall", outcall: "Outcall", hotel_visit: "Hotel visit", apartment_visit: "Apartment visit",
  dinner_date: "Dinner date", event_companion: "Event companion", overnight: "Overnight",
  weekend_booking: "Weekend booking", domestic_travel: "Domestic travel", international_travel: "International travel",
};
const humanLabel = (slug) => AVAILABILITY_LABELS[slug] || slug.replace(/_/g, " ");

const Field = ({ label, value }) => (
  <div><p className="text-overline mb-1.5">{label}</p><p className="text-[#e5e5e5]">{value}</p></div>
);

export default function ProfileDetails() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [serviceItemsCatalog, setServiceItemsCatalog] = useState([]);
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("Inappropriate content");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/profiles/${id}`);
      setData(data);
    } catch (e) { console.warn("[ProfileDetails] load error:", e); }
  }, [id]);

  useEffect(() => {
    load();
    (async () => {
      try { const { data } = await api.get("/meta/services"); setServiceItemsCatalog(data.service_items || []); } catch (e) { console.warn("[ProfileDetails.jsx] non-blocking error:", e); }
    })();
  }, [load]);

  if (!data) return <div className="max-w-7xl mx-auto px-4 py-20 text-[#737373]">{t.common.loading}</div>;

  const p = data.profile;
  const reviews = data.reviews;
  const photos = (p.photos?.length ? p.photos : [p.main_photo]).filter(Boolean);
  const verified = p.verification_level === "verified" || p.verification_status === "verified";
  const hasIncall = typeof p.incall_price === "number" && p.incall_price > 0;
  const hasOutcall = typeof p.outcall_price === "number" && p.outcall_price > 0;

  const submitReview = async () => {
    if (!user) return toast.error("Sign in to leave a review");
    try {
      await api.post("/reviews", { profile_id: p.id, rating: reviewRating, text: reviewText });
      toast.success("Review submitted — pending moderation");
      setReviewText("");
    } catch (e) { toast.error("Could not submit"); }
  };

  const submitReport = async () => {
    if (!user) return toast.error("Sign in to report");
    try {
      await api.post("/reports", { profile_id: p.id, reason: reportReason });
      toast.success("Report submitted");
      setReportOpen(false);
    } catch { toast.error("Action failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="profile-details">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* LEFT: Gallery + About */}
        <div className="lg:col-span-2 space-y-10">
          <div>
            <div className="relative aspect-[4/5] rounded-lg overflow-hidden card-luxe">
              <img src={resolveImageUrl(photos[galleryIdx])} onError={onImageError} alt={p.name} className="w-full h-full object-cover" data-testid="profile-main-photo" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />

              {/* Verified — diagonal corner ribbon (identity signal only) */}
              {verified && <VerifiedRibbon size="lg" data-testid="ribbon-details" />}

              {/* Independent / Agency tag stays in the top-LEFT so it's
                  visually independent from the verification ribbon. */}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-black/60 backdrop-blur-md text-gold border border-gold/40">
                  {p.is_independent ? t.profile.independent : t.profile.agency}
                </span>
              </div>
              <div className="absolute bottom-6 left-6">
                <p className="font-heading text-4xl sm:text-5xl text-white tracking-wide">{p.name}{p.age && <span className="text-2xl text-[#d4d4d4]">, {p.age}</span>}</p>
                <div className="flex items-center gap-3 text-sm text-[#d4d4d4] mt-2">
                  <MapPin className="w-4 h-4 text-gold" strokeWidth={1.5} /> {p.city}
                  <span className="text-[#737373]">•</span>
                  <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 text-gold fill-gold" /> {p.rating?.toFixed(1) || "—"}</span>
                  <span className="text-[#737373]">•</span>
                  <span className="flex items-center gap-1 text-emerald-300"><ShieldCheck className="w-3.5 h-3.5" /> {t.profile.trust} {p.trust_score}</span>
                </div>
              </div>
            </div>
            {photos.length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-3">
                {photos.map((src, i) => (
                  <button key={`${src}-${i}`} onClick={() => setGalleryIdx(i)} className={`aspect-square rounded overflow-hidden border ${i === galleryIdx ? "border-gold" : "border-white/5"}`} data-testid={`gallery-thumb-${i}`}>
                    <img
                      src={resolveImageUrl(src)}
                      onError={onImageError}
                      alt={`${p.name} ${i + 1}`}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <section data-testid="profile-about">
            <p className="text-overline mb-3">{t.profile.about}</p>
            <p className="font-body text-base text-[#d4d4d4] leading-relaxed">{p.description || "—"}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 text-sm">
              {p.languages?.length > 0 && (
                <Field label="Languages" value={p.languages.join(", ")} />
              )}
              {p.nationality && <Field label="Nationality" value={p.nationality} />}
              {p.height && <Field label="Height" value={`${p.height} cm`} />}
              {p.weight && <Field label="Weight" value={`${p.weight} kg`} />}
              {p.body_type && <Field label="Body type" value={p.body_type} />}
              {p.hair_color && <Field label="Hair" value={p.hair_color} />}
              {p.eye_color && <Field label="Eyes" value={p.eye_color} />}
              {p.bust_size && <Field label="Bust" value={p.bust_size} />}
              {p.smoking && <Field label="Smoking" value={p.smoking} />}
              {p.drinking && <Field label="Drinking" value={p.drinking} />}
              {p.availability && <Field label={t.profile.availability} value={p.availability} />}
            </div>
            {(p.tattoos || p.piercings) && (
              <div className="flex gap-2 mt-4">
                {p.tattoos && <span className="px-2.5 py-1 rounded-full text-xs bg-white/5 border border-white/10 text-[#a3a3a3]">Tattoos</span>}
                {p.piercings && <span className="px-2.5 py-1 rounded-full text-xs bg-white/5 border border-white/10 text-[#a3a3a3]">Piercings</span>}
              </div>
            )}
          </section>

          {p.availability_types?.length > 0 && (
            <section data-testid="profile-availability-types">
              <p className="text-overline mb-3">Availability</p>
              <div className="flex flex-wrap gap-2">
                {p.availability_types.map((a) => (
                  <span key={a} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-emerald/10 border border-emerald/30 text-emerald-200">
                    <Check className="w-3 h-3" /> {humanLabel(a)}
                  </span>
                ))}
              </div>
            </section>
          )}

          {(p.service_items?.length > 0 || p.services?.length > 0) && (
            <section data-testid="profile-services">
              <p className="text-overline mb-3">{t.profile.services}</p>
              {p.service_items?.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {p.service_items.map((si, idx) => {
                    const svc = (serviceItemsCatalog || []).find((s) => s.id === si.service_id);
                    if (!svc) return null;
                    const isExtra = si.mode === "extra";
                    return (
                      <div key={`${si.service_id}-${idx}`} className={`flex items-center justify-between px-3 py-2 rounded border ${isExtra ? "border-gold/40 bg-gold/5" : "border-emerald/30 bg-emerald/5"}`}>
                        <span className="text-sm text-[#e5e5e5]">{svc.name}</span>
                        <span className={`text-xs ${isExtra ? "text-gold" : "text-emerald-300"}`}>
                          {isExtra ? `+${si.price ?? "—"} ${si.currency || p.currency || "EUR"}` : "Included"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {p.services.map((s) => (
                    <span key={s} className="px-3 py-1.5 rounded-full text-xs bg-white/5 text-[#d4d4d4] border border-white/10">{s}</span>
                  ))}
                </div>
              )}
            </section>
          )}

          {p.videos?.length > 0 && (
            <section data-testid="profile-videos">
              <p className="text-overline mb-3">Videos</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {p.videos.map((url) => (
                  <div key={url} className="aspect-video card-luxe rounded-lg overflow-hidden">
                    <video src={url} controls className="w-full h-full object-cover bg-black" />
                  </div>
                ))}
              </div>
            </section>
          )}

          <section data-testid="profile-reviews">
            <p className="text-overline mb-3">{t.profile.reviews} ({reviews.length})</p>
            <div className="space-y-4">
              {reviews.map((r) => (
                <div key={r.id} className="card-luxe rounded-lg p-5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-white">{r.reviewer_name}</p>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => <Star key={`r-${r.id}-s${n}`} className={`w-3 h-3 ${n <= r.rating ? "text-gold fill-gold" : "text-[#444]"}`} strokeWidth={0} />)}
                    </div>
                  </div>
                  <p className="text-sm text-[#a3a3a3] leading-relaxed">{r.text}</p>
                </div>
              ))}
              {reviews.length === 0 && <p className="text-sm text-[#737373]">No approved reviews yet.</p>}
            </div>

            {user && (
              <div className="mt-6 card-luxe rounded-lg p-5">
                <p className="text-overline mb-3">Leave a review</p>
                <div className="flex gap-1 mb-3">
                  {[1,2,3,4,5].map((n) => (
                    <button key={n} onClick={() => setReviewRating(n)} data-testid={`review-rating-${n}`}>
                      <Star className={`w-5 h-5 ${n <= reviewRating ? "text-gold fill-gold" : "text-[#555]"}`} strokeWidth={0} />
                    </button>
                  ))}
                </div>
                <textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)} placeholder="Share your experience…" className="w-full bg-[#0a0a0a] border border-white/10 rounded-md p-3 text-sm text-white focus:border-gold/50 focus:outline-none" rows="3" data-testid="review-text" />
                <button onClick={submitReview} className="btn-gold rounded-md px-5 py-2 text-sm mt-3" data-testid="review-submit">{t.common.submit}</button>
              </div>
            )}
          </section>
        </div>

        {/* RIGHT: Sticky card */}
        <aside className="lg:sticky lg:top-24 self-start space-y-6">
          <div className="card-luxe rounded-lg p-6 bg-gradient-to-b from-[#1a1a1a] to-[#121212] border-gold/30">
            <p className="text-overline">{t.profile.rates}</p>
            {(hasIncall || hasOutcall) && (
              <div className="mt-4 grid grid-cols-2 gap-3" data-testid="profile-split-prices">
                {hasIncall ? (
                  <div className="rounded-lg border border-gold/30 bg-gold/5 p-3">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-gold/80">In-call</p>
                    <p className="font-heading text-xl text-gold mt-1">{p.incall_price} <span className="text-xs text-[#a3a3a3]">{p.currency || "EUR"}</span></p>
                  </div>
                ) : (
                  <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 opacity-60">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#737373]">In-call</p>
                    <p className="text-sm text-[#737373] mt-1">Not offered</p>
                  </div>
                )}
                {hasOutcall ? (
                  <div className="rounded-lg border border-gold/30 bg-gold/5 p-3">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-gold/80">Out-call</p>
                    <p className="font-heading text-xl text-gold mt-1">{p.outcall_price} <span className="text-xs text-[#a3a3a3]">{p.currency || "EUR"}</span></p>
                  </div>
                ) : (
                  <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 opacity-60">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#737373]">Out-call</p>
                    <p className="text-sm text-[#737373] mt-1">Not offered</p>
                  </div>
                )}
              </div>
            )}
            <div className="mt-4 space-y-3">
              {p.rates && Object.entries(p.rates).filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex justify-between items-center pb-3 border-b border-white/5 last:border-0">
                  <span className="text-sm text-[#d4d4d4]">{({
                    thirty_min: "30 minutes", hour: "1 hour", two_hour: "2 hours", three_hour: "3 hours",
                    dinner_date: "Dinner date", overnight: "Overnight", weekend: "Weekend", travel: "Travel (per day)",
                  })[k] || k.replace("_", " ")}</span>
                  <span className="font-heading text-lg text-gold">{v} {p.currency || "EUR"}</span>
                </div>
              ))}
              {(!p.rates || !Object.values(p.rates).filter(Boolean).length) && !hasIncall && !hasOutcall && (
                <p className="text-sm text-[#737373]">Contact for rates</p>
              )}
            </div>
            <div className="gold-divider my-5" />
            <p className="text-overline mb-3">{t.profile.contact}</p>
            <ContactButtons profile={p} />
          </div>

          <div className="card-luxe rounded-lg p-5">
            <p className="text-overline mb-3">Trust</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#a3a3a3]">Verification</span><span className={verified ? "text-emerald-300" : "text-[#737373]"}>{verified ? "Verified" : "Basic"}</span></div>
              <div className="flex justify-between"><span className="text-[#a3a3a3]">Trust score</span><span className="text-gold">{p.trust_score}/100</span></div>
              <div className="flex justify-between"><span className="text-[#a3a3a3]">Reviews</span><span className="text-white">{p.review_count}</span></div>
              <div className="flex justify-between"><span className="text-[#a3a3a3]">Rating</span><span className="text-white">{p.rating?.toFixed(1) || "—"}</span></div>
            </div>
          </div>

          <button onClick={() => setReportOpen(true)} className="w-full text-xs text-[#737373] hover:text-white inline-flex items-center justify-center gap-1.5 py-3" data-testid="report-btn">
            <Flag className="w-3 h-3" strokeWidth={1.5} /> {t.profile.report}
          </button>
        </aside>
      </div>

      {reportOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-4" onClick={() => setReportOpen(false)}>
          <div className="bg-[#121212] border border-white/10 rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <p className="font-heading text-2xl text-white">{t.profile.report}</p>
            <select value={reportReason} onChange={(e) => setReportReason(e.target.value)} className="w-full mt-4 bg-[#0a0a0a] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white" data-testid="report-reason">
              <option>Inappropriate content</option>
              <option>Suspicious profile</option>
              <option>Fake photos</option>
              <option>Harassment</option>
              <option>Other</option>
            </select>
            <div className="flex gap-2 mt-5">
              <button onClick={submitReport} className="flex-1 btn-gold rounded-md py-2 text-sm" data-testid="report-submit">{t.common.submit}</button>
              <button onClick={() => setReportOpen(false)} className="flex-1 btn-ghost-dark rounded-md py-2 text-sm">{t.common.cancel}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// ContactButtons — renders one large, touch-friendly action button per
// channel that the profile has BOTH enabled and filled. Anti-scrape: the
// phone number is masked visually until the user taps "Reveal".
// =============================================================================
function ContactButtons({ profile }) {
  const channels = activeContactChannels(profile);
  const det = profile?.contact_details || {};
  const [revealed, setRevealed] = useState(false);

  if (channels.length === 0) {
    return (
      <p className="text-sm text-[#737373] py-3 text-center" data-testid="contact-none">
        Contact details not yet provided.
      </p>
    );
  }

  const copyPhone = async () => {
    const num = "+" + toInternational(det.phone || det.whatsapp || det.viber);
    try {
      await navigator.clipboard.writeText(num);
      toast.success("Number copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const cls = "w-full rounded-md px-4 py-3 text-sm flex items-center justify-center gap-2 font-medium transition";
  const phoneCC = det.phone?.country_code || det.whatsapp?.country_code || det.viber?.country_code || "+382";
  const phoneDigits = det.phone?.number || det.whatsapp?.number || det.viber?.number || "";

  return (
    <div className="space-y-2" data-testid="contact-buttons">
      {channels.includes("whatsapp") && (
        <a
          href={whatsappHref(det.whatsapp)}
          target="_blank" rel="noreferrer noopener"
          className={`${cls} bg-emerald-600 hover:bg-emerald-500 text-white`}
          data-testid="contact-whatsapp"
        >
          <MessageCircle className="w-4 h-4" /> WhatsApp
        </a>
      )}
      {channels.includes("telegram") && (
        <a
          href={telegramHref(det.telegram?.username)}
          target="_blank" rel="noreferrer noopener"
          className={`${cls} bg-sky-600 hover:bg-sky-500 text-white`}
          data-testid="contact-telegram"
        >
          <Send className="w-4 h-4" /> Telegram
        </a>
      )}
      {channels.includes("phone") && (
        <a
          href={telHref(det.phone)}
          className={`${cls} btn-gold`}
          data-testid="contact-call"
        >
          <Phone className="w-4 h-4" /> Call {revealed ? `${phoneCC} ${phoneDigits}` : maskPhone(det.phone)}
        </a>
      )}
      {channels.includes("viber") && (
        <a
          href={viberHref(det.viber)}
          target="_blank" rel="noreferrer noopener"
          className={`${cls} bg-purple-700 hover:bg-purple-600 text-white`}
          data-testid="contact-viber"
        >
          <Phone className="w-4 h-4" /> Viber
        </a>
      )}
      {channels.includes("email") && (
        <a
          href={mailtoHref(det.email?.address)}
          className={`${cls} btn-ghost-dark`}
          data-testid="contact-email"
        >
          <Mail className="w-4 h-4" /> {det.email?.address}
        </a>
      )}
      {phoneDigits && (
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={() => setRevealed((r) => !r)}
            className="flex-1 btn-ghost-dark rounded-md px-3 py-2 text-xs"
            data-testid="contact-reveal"
          >{revealed ? "Hide number" : "Reveal number"}</button>
          <button
            type="button"
            onClick={copyPhone}
            className="flex-1 btn-ghost-dark rounded-md px-3 py-2 text-xs flex items-center justify-center gap-1"
            data-testid="contact-copy"
          ><Copy className="w-3 h-3" /> Copy</button>
        </div>
      )}
    </div>
  );
}
